import { NavBar, NavBarProps } from '@opengov/bnp-component-navbar';
import { useChatBotSnippet } from '@opengov/bnp-salesforce-chatbot';
import { IToastProps, Toast } from '@opengov/component-library-classic/classic';
import {
  IContextMenuComponentProvider,
  IContextMenuMetadata
} from '@opengov/component-library-classic/module/contextMenus/types';
import { IModalComponentProvider, IModalMetadata } from '@opengov/component-library-classic/module/modals/types';
import { Toast as CapitalToast, IToastProps as ICapitalToastProps } from '@opengov/component-library/dist';
import {
  AdminBar,
  EntitySwitcher,
  IAdminBarProps,
  IEntitySwitcherProps,
  INavigationBarProps,
  NavigationBar
} from '@opengov/components-navigation';
import classNames from 'classnames2';
import _ from 'lodash';
import React from 'react';

import type { ModuleMountOptions } from '@opengov/psp-contract';
import { useStore } from 'zustand';
import { accessTokenForNonReactContextsStore } from '../auth/accessTokenForNonReactContextsStore';
import { ISevereErrorSplashProps, SevereErrorSplash } from '../errors/components/SevereErrorSplash';
import { ApplicationContextElement, useApplicationContext } from '../navigation/useApplicationContext';
import { usePspOptions } from '../psp/context/psp.context';
import { isEmbeddedFromLocalStorageAndTopUrl } from '../utils/embedded';

import styles from './DefaultPageLayout.scss';
import { useWorkspaceSurfaceOffset } from './useWorkspaceSurfaceOffset';

export type IDefaultPageLayoutProps<MP, CMP> = {
  adminBarProps: IAdminBarProps;
  canCreateEntity?: boolean;
  contextMenuMetadata?: IContextMenuMetadata<CMP>;
  entitySwitcherProps: IEntitySwitcherProps;
  isToastOpen: boolean;
  isCapitalToastOpen: boolean;
  modalMetaData?: IModalMetadata<MP>;
  navigationBarProps: INavigationBarProps;
  globalNavBarProps?: NavBarProps;
  severeErrorSplash: ISevereErrorSplashProps;
  shouldShowAdminBar?: boolean;
  shouldShowEntitySwitcher?: boolean;
  shouldShowGlobalNavBar?: boolean;
  shouldShowModal?: boolean;
  showCaret?: boolean;
  showContextMenu?: boolean;
  toast: IToastProps;
  capitalToast: ICapitalToastProps;
  /**
   * Extra OG Assist application-context elements the hosting app contributes, appended after the
   * standard applicationName, applicationDisplayName, activeProduct, currentPage and currentPageTitle
   * elements. The agent server merges elements per key (last writer wins), so each app must use its
   * own namespaced keys.
   */
  agentApplicationContext?: ApplicationContextElement[];
  chatbotConfig?: {
    integrationKey: string;
    platformName: string;
    messageId: string;
    secretUrl: string;
  };
  viewElementsOnClick(): void;
  hideToast(): void;
  hideCapitalToast(): void;
  shouldShowCoABanner?: boolean;
};

export const createPageLayout =
  <MP extends {}, CMP extends {}>(
    modalComponentProvider?: IModalComponentProvider<MP>,
    contextMenuContentProvider?: IContextMenuComponentProvider<CMP>
  ): React.FC<IDefaultPageLayoutProps<MP, CMP>> =>
  (props) => {
    const pspOptions = usePspOptions();
    const isPsp = !_.isNil(pspOptions);
    const embedded = isEmbeddedFromLocalStorageAndTopUrl();
    const applicationContext = useApplicationContext(
      props.globalNavBarProps?.activeAppKey,
      props.agentApplicationContext
    );
    // Use a callback-ref via state so the NavBar re-renders (and portals) once the
    // DOM node is attached. A plain useRef is null on first render, causing
    // createPortal(node, null) to no-op until some unrelated rerender.
    const [drawerPortalTarget, setDrawerPortalTarget] = React.useState<HTMLDivElement | null>(null);
    const [isOgAssistDrawerOpen, setIsOgAssistDrawerOpen] = React.useState(false);
    // Ref to the flex row so we can publish the live Workspace-surface width to CSS,
    // sliding the open OG Assist drawer flush beside the Workspace rail/panel.
    const contentWithDrawerRef = React.useRef<HTMLDivElement>(null);
    useWorkspaceSurfaceOffset(contentWithDrawerRef, isOgAssistDrawerOpen);
    const drawerPortalTargetRef = React.useMemo<React.RefObject<HTMLDivElement>>(
      () => ({ current: drawerPortalTarget }),
      [drawerPortalTarget]
    );
    const ogAssistDrawerOptions = React.useMemo(
      () => ({ drawerPortalTargetRef, onDrawerToggle: setIsOgAssistDrawerOpen }),
      [drawerPortalTargetRef]
    );

    useChatBotSnippet({
      firstName: props.navigationBarProps.session.first_name,
      lastName: props.navigationBarProps.session.last_name,
      email: props.navigationBarProps.session.email,
      integrationKey: props.chatbotConfig?.integrationKey ?? '',
      platformName: props.chatbotConfig?.platformName ?? '',
      messageId: props.chatbotConfig?.messageId ?? '',
      secretUrl: props.chatbotConfig?.secretUrl ?? ''
    });

    const withGlobalNavBar = Boolean(props.shouldShowGlobalNavBar && props.globalNavBarProps);
    const withNavigationBar = !(_.isUndefined(props.shouldShowGlobalNavBar) || withGlobalNavBar) && !isPsp;
    const withAdminBar = props.shouldShowAdminBar && withNavigationBar && !isPsp;
    const withCoABanner = props.shouldShowCoABanner && withGlobalNavBar;
    const showOgAssistSlot = withGlobalNavBar && !embedded;

    const globalNavBarProps = useNavbarAuth0ConfigOverride({
      navbarProps: props.globalNavBarProps,
      pspOptions
    });

    const hasNavBars = !embedded;

    const mainContent = (
      <main id="main" className={styles.main} tabIndex={-1}>
        <SevereErrorSplash {...props.severeErrorSplash} />
        {!props.severeErrorSplash.shouldShow && props.children}
      </main>
    );

    // Portal target for the OG Assist drawer. Only rendered when the global NavBar
    // owns the drawer (not embedded). The drawer is portaled here so it sits beside
    // <main> in the flex row, pushing content aside instead of overlaying it.
    // Width collapses to 0 when the drawer is closed.
    const contentWithDrawer = showOgAssistSlot ? (
      <div ref={contentWithDrawerRef} className={styles.contentWithDrawer}>
        {mainContent}
        <div
          ref={setDrawerPortalTarget}
          data-testid="og-assist-drawer-slot"
          className={classNames(styles.ogAssistDrawerSlot, {
            [styles.ogAssistDrawerSlotOpen]: isOgAssistDrawerOpen
          })}
        />
      </div>
    ) : (
      mainContent
    );

    return (
      <div
        className={classNames(
          styles.page,
          hasNavBars
            ? {
                [styles.withGlobalNavBar]: withGlobalNavBar,
                [styles.withNavigationBar]: withNavigationBar,
                [styles.withAdminBar]: withAdminBar,
                [styles.withCoABanner]: withCoABanner
              }
            : undefined
        )}
      >
        {props.shouldShowModal && modalComponentProvider(props.modalMetaData)}

        {props.showContextMenu && contextMenuContentProvider && contextMenuContentProvider(props.contextMenuMetadata)}

        {showOgAssistSlot ? (
          <div className={styles.globalNavBarContainer}>
            <NavBar
              {...globalNavBarProps}
              applicationContext={applicationContext}
              ogAssistDrawerOptions={ogAssistDrawerOptions}
              banner={
                props.shouldShowCoABanner ? (
                  <NavBar.CoAMigrationBanner entitySubdomain={props.globalNavBarProps.entitySubdomain} />
                ) : undefined
              }
            />
          </div>
        ) : null}
        {withNavigationBar && !embedded ? (
          <NavigationBar
            currentAppPathname={props.navigationBarProps.currentAppPathname}
            currentEntity={props.navigationBarProps.currentEntity}
            menuItems={props.navigationBarProps.menuItems}
            session={props.navigationBarProps.session}
            helpLink="https://opengov.my.site.com/support/services/auth/sso/OpenGov_Unified_Login"
            showAppSwitcher={props.navigationBarProps.showAppSwitcher}
            activeProduct={props.navigationBarProps.activeProduct}
          />
        ) : null}

        {withAdminBar && !embedded ? (
          <div className={styles.adminBarContainer}>
            <AdminBar
              userDemotedAt={props.navigationBarProps?.session.demote_at}
              entityName={props.adminBarProps.entityName}
              environmentName={props.adminBarProps.environmentName}
              showEnvironment={props.adminBarProps.showEnvironment}
              onSwitchEntity={props.adminBarProps.onSwitchEntity}
            />
          </div>
        ) : null}

        {/* tslint:disable-next-line:react-a11y-event-has-role */}
        <div id="viewElements" onClick={props.viewElementsOnClick} className={styles.container}>
          {props.shouldShowEntitySwitcher && !embedded && (
            <EntitySwitcher
              canCreateEntity={props.canCreateEntity}
              createEntityLink="/entity/create"
              onCreateEntity={() => ((window as any).location = '/entity/create')}
              currentAppPathname={props.entitySwitcherProps.currentAppPathname}
              currentPagePath={props.entitySwitcherProps.currentPagePath}
              entities={props.entitySwitcherProps.entities}
              onClose={props.entitySwitcherProps.onClose}
            />
          )}

          {contentWithDrawer}

          {props.isToastOpen && (
            <Toast
              type={props.toast.type}
              headerText={props.toast.headerText}
              bodyText={props.toast.bodyText}
              actionText={''}
              handleClose={props.hideToast}
              dataTest={props.toast.dataTest}
            />
          )}

          {props.isCapitalToastOpen && <CapitalToast {...props.capitalToast} onClose={props.hideCapitalToast} />}
        </div>
      </div>
    );
  };

function useNavbarAuth0ConfigOverride({
  navbarProps,
  pspOptions
}: {
  navbarProps: NavBarProps;
  pspOptions?: ModuleMountOptions;
}) {
  const accessToken = useStore(accessTokenForNonReactContextsStore, (state) => state.accessToken);

  const isPsp = !_.isNil(pspOptions);

  if (!isPsp) {
    return navbarProps;
  }

  return {
    ...navbarProps,
    contextSwitcherOptions: {
      ...navbarProps.contextSwitcherOptions,
      auth0Config: {
        auth0token: accessToken,
        auth0skipRedirectCallback: true
      }
    }
  };
}
